import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '../services/notificationService';

export const useNotifications = (params = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchNotifications = useCallback(async (fetchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getNotifications({
        ...params,
        ...fetchParams
      });
      
      setNotifications(response.data.data || []);
      
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(params);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      await fetchNotifications(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark notification as read';
    }
  };

  const markAsUnread = async (id) => {
    try {
      await notificationService.markAsUnread(id);
      await fetchNotifications(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark notification as unread';
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await fetchNotifications(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to mark all notifications as read';
    }
  };

  const deleteNotification = async (id) => {
    try {
      await notificationService.deleteNotification(id);
      await fetchNotifications(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete notification';
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await notificationService.deleteAllNotifications();
      await fetchNotifications(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete all notifications';
    }
  };

  const refreshNotifications = () => {
    fetchNotifications();
  };

  const loadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchNotifications({ page: pagination.page + 1 });
    }
  };

  return {
    notifications,
    loading,
    error,
    pagination,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    refreshNotifications,
    loadMore,
    fetchNotifications
  };
};

export const useUnreadNotifications = (params = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getUnreadNotifications(params);
      setNotifications(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch unread notifications');
      console.error('Error fetching unread notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadNotifications(params);
  }, [fetchUnreadNotifications]);

  return {
    notifications,
    loading,
    error,
    refreshUnreadNotifications: fetchUnreadNotifications
  };
};

export const useNotificationCount = () => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotificationCount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getNotificationCount();
      setCount(response.data.data?.count || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch notification count');
      console.error('Error fetching notification count:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Disabled: Polling for notification count
    // fetchNotificationCount();
    
    // Refresh count every 30 seconds
    // const interval = setInterval(fetchNotificationCount, 30000);
    
    // return () => clearInterval(interval);
  }, [fetchNotificationCount]);

  return {
    count,
    loading,
    error,
    refreshNotificationCount: fetchNotificationCount
  };
};

export const useUnreadCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.data.data?.count || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch unread count');
      console.error('Error fetching unread count:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Disabled: Polling for unread count
    // fetchUnreadCount();
    
    // Refresh unread count every 30 seconds
    // const interval = setInterval(fetchUnreadCount, 30000);
    
    // return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    loading,
    error,
    refreshUnreadCount: fetchUnreadCount
  };
};

export const useNotificationPreferences = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await notificationService.getNotificationPreferences();
      setPreferences(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch notification preferences');
      console.error('Error fetching notification preferences:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreferences = async (data) => {
    try {
      const response = await notificationService.updateNotificationPreferences(data);
      setPreferences(response.data.data);
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update notification preferences';
    }
  };

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    refreshPreferences: fetchPreferences
  };
};
